import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { createDonationCheckout } from "@workspace/api-client-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DollarSign, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useDesignatedCheckout } from "@/hooks/use-debt-profiles";

const AMOUNTS = [10, 25, 50, 100, 250];

interface DonateModalProps {
  pool: { id: number; name: string; theme: string };
  onClose: () => void;
  profile?: { slug: string; alias: string };
}

export default function DonateModal({ pool, onClose, profile }: DonateModalProps) {
  const [amount, setAmount] = useState<number>(50);
  const [customAmount, setCustomAmount] = useState<string>("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [tosAgreed, setTosAgreed] = useState(false);
  const { toast } = useToast();
  const designatedCheckout = useDesignatedCheckout();

  const finalAmount = customAmount ? parseFloat(customAmount) : amount;

  const checkout = useMutation({
    mutationFn: () =>
      createDonationCheckout({
        poolId: pool.id,
        amount: finalAmount,
        isRecurring,
        tosAgreed,
      }),
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (err: Error & { data?: { error?: string } }) => {
      toast({
        title: "Payment unavailable",
        description: err?.data?.error || "Stripe payments not configured yet.",
        variant: "destructive",
      });
    },
  });

  function submitDonation() {
    if (profile) {
      designatedCheckout.mutate({ poolId: pool.id, amount: finalAmount, profileSlug: profile.slug }, {
        onSuccess: data => { window.location.href = data.url; },
        onError: error => toast({ title: "Payment unavailable", description: error.message, variant: "destructive" }),
      });
    } else checkout.mutate();
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-md"
        aria-labelledby="donate-dialog-title"
        aria-modal="true"
      >
        <DialogHeader>
          <DialogTitle id="donate-dialog-title">{profile ? `Give to ${profile.alias}` : `Donate to ${pool.name}`}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <p className="text-sm text-muted-foreground mb-3">{pool.theme}</p>
            {profile && <p className="text-sm text-muted-foreground">A one-time designation through the {pool.name} pool. If this profile becomes unavailable, checkout may not proceed; you can still give directly to the pool.</p>}
          </div>

          <div>
            <Label className="mb-2 block">Select Amount</Label>
            <div className="grid grid-cols-5 gap-2" role="group" aria-label="Preset donation amounts">
              {AMOUNTS.map((a) => (
                <button
                  key={a}
                  onClick={() => { setAmount(a); setCustomAmount(""); }}
                  aria-pressed={amount === a && !customAmount}
                  aria-label={`$${a}${amount === a && !customAmount ? " — selected" : ""}`}
                  className={`py-2 rounded-lg text-sm font-medium border transition-colors ${
                    amount === a && !customAmount
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:border-primary/50"
                  }`}
                >
                  ${a}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label htmlFor="custom-amount" className="mb-2 block">Or enter custom amount</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                id="custom-amount"
                type="number"
                min="1"
                step="0.01"
                placeholder="0.00"
                value={customAmount}
                onChange={(e) => { setCustomAmount(e.target.value); setAmount(0); }}
                className="w-full pl-9 pr-4 py-2 border rounded-lg bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {!profile && <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <div className="font-medium text-sm">Monthly Recurring</div>
              <div className="text-xs text-muted-foreground">Donate every month automatically</div>
            </div>
            <Switch checked={isRecurring} onCheckedChange={setIsRecurring} />
          </div>}

          <div className="rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
            <strong>${finalAmount > 0 ? finalAmount.toFixed(2) : "0.00"}</strong>
            {isRecurring ? "/month" : " one-time"} to <strong>{pool.name}</strong>{profile && <> designated for <strong>{profile.alias}</strong></>}
          </div>

          {/* TOS consent checkbox */}
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={tosAgreed}
              onChange={(e) => setTosAgreed(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border accent-primary cursor-pointer shrink-0"
            />
            <span className="text-sm text-muted-foreground leading-snug">
              I agree to the{" "}
              <a
                href="/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-foreground"
                onClick={(e) => e.stopPropagation()}
              >
                Terms of Service
              </a>{" "}
              and understand this is a donation to a 501(c)(3) pending organization. Donations may be tax-deductible — consult your tax advisor.
            </span>
          </label>

          <Button
            className="w-full"
            onClick={submitDonation}
            disabled={checkout.isPending || designatedCheckout.isPending || !Number.isFinite(finalAmount) || finalAmount <= 0 || !tosAgreed}
          >
            {checkout.isPending || designatedCheckout.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
            ) : (
              `Donate $${finalAmount > 0 ? finalAmount.toFixed(2) : "0.00"}${isRecurring ? "/mo" : ""}`
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Secure checkout via Stripe · Tax-deductible receipt issued
          </p>
          <p className="text-xs text-center text-muted-foreground" style={{ opacity: 0.7, marginTop: "-8px" }}>
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ textDecoration: "underline" }}>Privacy Policy</a>
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
